import type { BookingInterface, BookingUserInterface } from "types/booking";
import { BookingStatus } from "types/booking";
import { Role } from "types/user";
import CalculOpperator from "helpers/CalculOpperator";

type BookingUserName = Pick<BookingUserInterface, "first_name" | "last_name">;

export const getFullName = (user: BookingUserName): string =>
    [user.first_name, user.last_name].filter(Boolean).join(" ");

export const getFirstName = (user: BookingUserName): string => user.first_name || getFullName(user);

export const isSupportedRole = (role: unknown): role is Role.CLIENT | Role.PROVIDER =>
    role === Role.CLIENT || role === Role.PROVIDER;

const frenchDateFormatter = new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
});

export const formatDuration = (hours: number): string => {
    const totalMinutes = Math.round(hours * 60);
    const hourPart = Math.floor(totalMinutes / 60);
    const minutePart = totalMinutes % 60;

    if (minutePart === 0) {
        return `${hourPart}h`;
    }

    return `${hourPart}h${String(minutePart).padStart(2, "0")}`;
};

export const formatServiceHistoryTime = (startTime: string): string => {
    const [hours, minutes = "00"] = startTime.split(":");

    return `${hours}:${minutes}`;
};

export const getBookingCalendarDate = (date: string): Date | null => {
    const dateOnlyMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);

    if (!dateOnlyMatch) {
        return null;
    }

    return new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]));
};

export const getTodayCalendarDate = (): Date => {
    const now = new Date();

    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

export const getBookingDateTime = (booking: Pick<BookingInterface, "date" | "start_time">): Date => {
    const [hours = "0", minutes = "0", seconds = "0"] = booking.start_time.split(":");
    const dateOnlyMatch = booking.date.match(/^(\d{4})-(\d{2})-(\d{2})$/);

    if (!dateOnlyMatch) {
        return new Date(booking.date);
    }

    return new Date(
        Number(dateOnlyMatch[1]),
        Number(dateOnlyMatch[2]) - 1,
        Number(dateOnlyMatch[3]),
        Number(hours),
        Number(minutes),
        Number(seconds),
    );
};

export const isOnOrBeforeBookingDate = (booking: Pick<BookingInterface, "date">): boolean => {
    const bookingDate = getBookingCalendarDate(booking.date);

    if (!bookingDate) {
        return false;
    }

    return getTodayCalendarDate().getTime() <= bookingDate.getTime();
};

export const isBookingOnSameDay = (booking: Pick<BookingInterface, "date">): boolean => {
    const bookingDate = getBookingCalendarDate(booking.date);

    if (!bookingDate) {
        return false;
    }

    return getTodayCalendarDate().getTime() === bookingDate.getTime();
};

export const isBookingFinalized = (booking: Pick<BookingInterface, "status">): boolean =>
    booking.status === BookingStatus.CLOSED || booking.status === BookingStatus.CANCELLED;

export const canCancelBooking = (
    booking: Pick<BookingInterface, "date" | "status">,
): boolean => !isBookingFinalized(booking) && isOnOrBeforeBookingDate(booking);

export const formatServiceHistoryDate = (date: string): string => {
    const dateOnlyMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const dateValue = dateOnlyMatch
        ? new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]))
        : new Date(date);
    const formatted = frenchDateFormatter.format(dateValue);

    return formatted.replace(/\p{L}+/u, (month) => month.charAt(0).toUpperCase() + month.slice(1));
};

const parseAmount = (amount: string | number | undefined): number => {
    const parsedAmount = Number.parseFloat(String(amount ?? ""));

    return Number.isFinite(parsedAmount) ? parsedAmount : 0;
};

export const getProviderBookingAmount = (booking: Pick<BookingInterface, "price" | "nb_hours">): number =>
    parseAmount(booking.price) * booking.nb_hours;

export const getHourlyRateAmount = (booking: Pick<BookingInterface, "price">): number =>
    parseAmount(booking.price);

export const getSocialContributionsAmount = (
    booking: Pick<BookingInterface, "employee_contributions_ef" | "employer_contributions">,
): number => parseAmount(booking.employee_contributions_ef) + parseAmount(booking.employer_contributions);

export const getManagementFeesAmount = (
    booking: Pick<BookingInterface, "price" | "nb_hours">,
): number => {
    const [managementFees] = new CalculOpperator(null).computeTTC(getProviderBookingAmount(booking));

    return managementFees;
};

export const getClientServiceTotalAmount = (
    booking: Pick<
        BookingInterface,
        "price" | "nb_hours" | "employee_contributions_ef" | "employer_contributions"
    >,
): number =>
    getProviderBookingAmount(booking)
    + getSocialContributionsAmount(booking)
    + getManagementFeesAmount(booking);

export const getClientBookingAmount = (
    booking: Pick<
        BookingInterface,
        "amount" | "price" | "nb_hours" | "employee_contributions_ef" | "employer_contributions"
    >,
): number => {
    const paidAmount = parseAmount(booking.amount);

    if (paidAmount > 0) {
        return paidAmount;
    }

    return getProviderBookingAmount(booking) + getSocialContributionsAmount(booking);
};

export const getBookingAmount = (
    booking: Pick<
        BookingInterface,
        "amount" | "price" | "nb_hours" | "employee_contributions_ef" | "employer_contributions"
    >,
    role: Role.CLIENT | Role.PROVIDER,
): number => role === Role.PROVIDER ? getProviderBookingAmount(booking) : getClientBookingAmount(booking);

export const getTaxCreditAmount = (
    booking: Pick<
        BookingInterface,
        "price" | "nb_hours" | "employee_contributions_ef" | "employer_contributions"
    >,
): number => {
    const providerAmount = getProviderBookingAmount(booking);
    const socialContributions = getSocialContributionsAmount(booking);

    return (providerAmount + socialContributions) / 2;
};

export const getClientServiceTotalAfterTaxCredit = (
    booking: Pick<
        BookingInterface,
        "price" | "nb_hours" | "employee_contributions_ef" | "employer_contributions"
    >,
): number => getClientServiceTotalAmount(booking) - getTaxCreditAmount(booking);

export const formatEuroAmount = (amount: number): string =>
    `${(Number.isFinite(amount) ? amount : 0).toFixed(2)} €`;

export const isCesuPlusActive = (
    booking: Pick<BookingInterface, "activate_cesu_plus" | "cesu_plus_status">,
): boolean => booking.activate_cesu_plus === "1" || Boolean(booking.cesu_plus_status);
